import React from "react";
import moment from "moment";
import { Divider, Typography } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { AccommodationPaymentMethod } from "@bonliva-crm/shared";
import { toSEK } from "@utils/formatCurrency";
import { AccommodationWeekLine } from "./utils";

const formatDate = (date: Date | null) =>
  date ? moment.utc(date).format("YYYY-MM-DD") : "–";

// Pay on arrival is the consultant's; Bonliva pays the other two and gets the money back.
const AMOUNT_NOTE: Partial<Record<AccommodationPaymentMethod, string>> = {
  [AccommodationPaymentMethod.PayOnArrival]: locale.accommodationNotPaidByBonliva,
  [AccommodationPaymentMethod.DeductFromSalary]: locale.accommodationDeductedFromSalary,
  [AccommodationPaymentMethod.InvoiceSubcontractor]:
    locale.accommodationChargedToSubcontractor
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 8 }}>
    <Typography variant="body2">{label}</Typography>
    <Typography
      variant="body2"
      style={{ flex: "1 0 auto", textAlign: "right", maxWidth: "100%" }}
    >
      {value}
    </Typography>
  </div>
);

/** Read-only breakdown of an ERP tax week into the reservations it is made of. */
export const ErpTaxWeekLines = ({ lines }: { lines: AccommodationWeekLine[] }) => (
  <div style={{ marginTop: 8 }} data-test="erp-tax-week-lines">
    <Typography variant="subtitle2">{locale.accommodationReservations}</Typography>
    {lines.map((line, i) => {
      const note = AMOUNT_NOTE[line.paymentMethod as AccommodationPaymentMethod];
      const amount = (value: number) =>
        note ? `${toSEK(value)} (${note})` : toSEK(value);
      return (
        <div key={line.reservationId ?? i} data-test="erp-tax-week-line">
          <Divider style={{ margin: "8px 0" }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {[
              line.accommodationName,
              line.city,
              line.area > 0 ? `${line.area} kvm` : null
            ]
              .filter(Boolean)
              .join(", ")}
          </Typography>
          <Row
            label={`${locale.FromAcc} – ${locale.ToAcc}`}
            value={`${formatDate(line.startDate)} – ${formatDate(line.endDate)}`}
          />
          <Row label="Nätter" value={line.nights} />
          <Row label={locale.accommodationStayedNights} value={line.stayedNights} />
          <Row label={locale.TotalCost2} value={amount(line.cost)} />
          {line.extraCost > 0 && (
            <Row
              label={locale.accommodationExtraCost}
              value={[amount(line.extraCost), line.extraDescription]
                .filter(Boolean)
                .join(" – ")}
            />
          )}
          <Row
            label={locale.accommodationBenefitTax}
            value={
              line.paymentMethod === AccommodationPaymentMethod.DeductFromSalary
                ? locale.accommodationNotBenefitTaxed
                : toSEK(line.tax)
            }
          />
          <Row
            label={locale.accommodationPaidBy}
            value={
              locale.accommodationPaymentMethods[
                line.paymentMethod as AccommodationPaymentMethod
              ] ?? line.paymentMethod
            }
          />
        </div>
      );
    })}
  </div>
);
